/**
 * IFRAP-AI.fr - Editorial KPI Data
 * KPIs enrichis avec contexte éditorial IFRAP
 * TOUJOURS sourcé, daté, avec origine
 */

export const KPI_STATUS = {
  REAL: 'real',
  MOCK: 'mock',
  ESTIMATE: 'estimate',
  TO_VALIDATE: 'to_validate',
  STALE: 'stale',
};

export const KPI_STATUS_LABELS = {
  real: { label: 'RÉEL', color: 'bg-green-500/20 text-green-600 border-green-500/30' },
  mock: { label: 'MOCK', color: 'bg-orange-500/20 text-orange-500 border-orange-500/30' },
  estimate: { label: 'ESTIMATION', color: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
  to_validate: { label: 'À VALIDER', color: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30' },
  stale: { label: 'PÉRIMÉE', color: 'bg-red-500/20 text-red-600 border-red-500/30 animate-pulse' },
};

export const KPI_ORIGIN = {
  BOOK: 'ifrap',
  IFRAP: 'ifrap',
  EMERGENT: 'emergent',
};

// ============================================================================
// KPIs "À LA UNE" - Indicateurs stratégiques
// ============================================================================

export const HEADLINE_KPIS = [
  {
    id: 'dette_etat',
    label: 'Dette publique',
    value: '115,6',
    unit: '% PIB',
    subValue: '3 460 Mds€',
    trend: 'up',
    trendLabel: '+154 Mds en 1 an',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'critical',
    source: 'INSEE, dette Maastricht T4 2025',
    sourceUrl: 'https://www.insee.fr/fr/statistiques/8955019',
    date: 'T4 2025',
    editorial: {
      quote: "3 460 milliards de dette : la France reste sur une trajectoire insoutenable.",
      whyImportant: "Chaque point de hausse des taux coûte 2,5 milliards supplémentaires par an.",
      ifrapView: "La dette française est devenue incontrôlable sans réformes structurelles.",
      watchFor: "Prévision 118-120% du PIB pour fin 2026.",
    },
  },
  {
    id: 'deficit_public',
    label: 'Déficit budgétaire',
    value: '-5,1',
    unit: '% PIB',
    subValue: '-152 Mds€',
    trend: 'down',
    trendLabel: 'Hors critères UE',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'critical',
    source: 'INSEE, comptes nationaux définitifs',
    sourceUrl: 'https://www.insee.fr/fr/statistiques/8956575',
    date: '2025 (définitif)',
    editorial: {
      quote: "50 ans de déficits ininterrompus depuis 1974.",
      whyImportant: "50 ans de déficits ont conduit à l'accumulation de 3 460 Mds€ de dette.",
      ifrapView: "Mieux que prévu (-5,4%) grâce aux recettes, mais tous les soldes restent dans le rouge.",
      watchFor: "Objectif gouvernemental autour de -5% pour 2026.",
    },
  },
  {
    id: 'depenses_publiques',
    label: 'Dépenses publiques',
    value: '58,2',
    unit: '% PIB',
    subValue: '~1 740 Mds€',
    trend: 'up',
    trendLabel: 'Record européen',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'high',
    source: 'INSEE, comptes nationaux 2025',
    sourceUrl: 'https://www.insee.fr/fr/statistiques/2381414',
    date: '2025 (définitif)',
    editorial: {
      quote: "58,2% du PIB : la France creuse encore l'écart avec ses voisins européens.",
      whyImportant: "Plus on dépense, plus on prélève, moins l'économie est compétitive.",
      ifrapView: "Hausse de 1,2 point vs 2024 (57,0%). La France s'éloigne de la maîtrise budgétaire.",
      watchFor: "Trajectoire de réduction promise par le gouvernement.",
    },
  },
  {
    id: 'deficit_secu',
    label: 'Déficit Sécurité Sociale',
    value: '-23',
    unit: 'Mds€',
    subValue: 'Doublé en 2 ans',
    trend: 'down',
    trendLabel: '-7,7 Mds vs 2024',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'critical',
    source: 'PLFSS 2026, Cour des comptes',
    sourceUrl: 'https://www.vie-publique.fr/en-bref/300727-comptes-de-la-securite-sociale',
    date: '2025',
    editorial: {
      quote: "Le déficit de la Sécu a plus que doublé en deux ans : 10,8 Mds en 2023, 23 Mds en 2025.",
      whyImportant: "Maladie : -17,2 Mds. Vieillesse : -6,3 Mds. Famille : +0,8 Mds.",
      ifrapView: "Les mesures non financées du Ségur de la santé coûtent 12,6 Mds€/an.",
      watchFor: "Trajectoire PLFSS 2026 : objectif -17,4 Mds€. Crédible ?",
    },
  },
  {
    id: 'dette_sociale',
    label: 'Dette sociale (CADES)',
    value: '122',
    unit: 'Mds€',
    subValue: 'Restant à rembourser',
    trend: 'down',
    trendLabel: 'Échéance 2033',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'high',
    source: 'CADES, rapport annuel 2025',
    sourceUrl: 'https://www.cades.fr/fr/',
    date: 'Fin 2025',
    editorial: {
      quote: "396 milliards de dette sociale depuis 1996. 274 milliards déjà amortis.",
      whyImportant: "La CRDS (0,5% sur tous les revenus) finance le remboursement jusqu'en 2033.",
      ifrapView: "+15 Mds€ de transfert prévu dans le PLFSS 2026. Le cercle vicieux continue.",
      watchFor: "Capacité de la CADES à absorber les nouveaux déficits.",
    },
  },
  {
    id: 'pensions_fonctionnaires',
    label: 'Pensions fonctionnaires',
    value: '68',
    unit: 'Mds€/an',
    subValue: 'CAS Pensions',
    trend: 'up',
    trendLabel: 'Taux employeur 82%',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'high',
    source: 'IFRAP / Rapporteur du budget, CAS Pensions 2025',
    sourceUrl: 'https://www.ifrap.org/retraite/le-rapporteur-du-budget-reconnait-15-milliards-de-deficit-des-pensions-publiques-detat',
    date: '2025',
    editorial: {
      quote: "15,4 Mds€ de déficit caché des pensions publiques d'État.",
      whyImportant: "Le taux employeur État est de 82,28% vs 16,5% dans le privé.",
      ifrapView: "Le rapporteur du budget reconnaît lui-même 15 milliards de déficit.",
      watchFor: "Hausse prévue du taux employeur à 82,28% en 2026.",
    },
  },
  {
    id: 'balance_commerciale',
    label: 'Balance commerciale',
    value: '-69,2',
    unit: 'Mds€',
    subValue: 'En amélioration vs 2024',
    trend: 'up',
    trendLabel: '-13% vs N-1',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'high',
    source: 'Douanes françaises, bilan 2025',
    sourceUrl: 'https://lekiosque.finances.gouv.fr',
    date: '2025',
    editorial: {
      quote: "Le déficit commercial se réduit mais reste préoccupant.",
      whyImportant: "Un déficit commercial chronique appauvrit le pays année après année.",
      ifrapView: "La réindustrialisation reste le levier principal pour résorber le déficit.",
      watchFor: "Balance énergétique et exportations industrielles.",
    },
  },
  {
    id: 'chomage',
    label: 'Taux de chômage',
    value: '7,9',
    unit: '%',
    subValue: '2,5 millions de personnes',
    trend: 'up',
    trendLabel: '+0,6 pt en 1 an',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.IFRAP,
    criticality: 'high',
    source: 'INSEE, Enquête emploi T4 2025',
    sourceUrl: 'https://www.insee.fr/fr/statistiques/8735266',
    date: 'T4 2025',
    editorial: {
      quote: "Le chômage repart à la hausse : 7,9% au T4 2025, +0,6 point en un an.",
      whyImportant: "Un chômeur coûte et ne contribue pas : double peine pour les finances.",
      ifrapView: "La remontée du chômage confirme l'échec des politiques de l'emploi.",
      watchFor: "Prévision INSEE : 7,8% au T2 2026. Chômage des jeunes à 21,5%.",
    },
  },
  {
    id: 'inflation',
    label: 'Inflation',
    value: '0,8',
    unit: '% a/a',
    subValue: 'Glissement annuel',
    trend: 'down',
    trendLabel: 'En baisse',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.EMERGENT,
    criticality: 'medium',
    source: 'INSEE, Indice des prix à la consommation',
    sourceUrl: 'https://www.insee.fr/fr/statistiques/8734269',
    date: 'Mars 2026',
    editorial: {
      quote: "L'inflation reste sous 1% : +0,8% en mars 2026 sur un an.",
      whyImportant: "Une inflation maîtrisée soutient le pouvoir d'achat.",
      ifrapView: "La vigilance reste de mise sur les prix de l'énergie.",
      watchFor: "Inflation sous-jacente à +1,3%. Prix de l'énergie en recul (-1,5% en mars).",
    },
  },
  {
    id: 'pib',
    label: 'PIB France',
    value: '2 990',
    unit: 'Mds€',
    subValue: 'Richesse nationale',
    trend: 'up',
    trendLabel: '+0,9% en 2025',
    status: KPI_STATUS.REAL,
    origin: KPI_ORIGIN.EMERGENT,
    criticality: 'high',
    source: 'INSEE, Comptes nationaux 2025',
    sourceUrl: 'https://www.insee.fr/fr/statistiques/8885657',
    date: '2025 (définitif)',
    editorial: {
      quote: "La France produit moins et dépense plus : l'équation impossible.",
      whyImportant: "Sans croissance, pas de recettes fiscales, pas de désendettement.",
      ifrapView: "La croissance doit venir de l'investissement privé, pas de la dépense publique.",
      watchFor: "Prévision +1,0% à +1,1% pour 2026.",
    },
  },
];

// ============================================================================
// BLOCS THÉMATIQUES - Toujours sourcés et datés
// ============================================================================

export const BLOC_DETTE = {
  id: 'dette_deficit',
  title: 'Dette & Déficit',
  subtitle: "L'urgence absolue",
  color: '#E1000F',
  icon: 'trending-up',
  kpis: [
    { id: 'dette_etat', label: 'Dette / PIB', value: '115,6', unit: '% PIB', comparison: 'vs 64% Allemagne', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'INSEE T4 2025', sourceUrl: 'https://www.insee.fr/fr/statistiques/8955019' },
    { id: 'charge_dette', label: 'Charge de la dette', value: '60', unit: 'Mds€/an', comparison: 'Intérêts annuels', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'PLF 2026', sourceUrl: 'https://www.budget.gouv.fr' },
    { id: 'deficit_structurel', label: 'Déficit structurel', value: '-4,2', unit: '% PIB', comparison: 'Hors conjoncture', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'Commission européenne, prévisions 2025', sourceUrl: 'https://economy-finance.ec.europa.eu/economic-surveillance-eu-economies/france_en' },
    { id: 'deficit_secu_bloc', label: 'Déficit Sécu', value: '-23', unit: 'Mds€', comparison: 'Doublé en 2 ans', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'PLFSS 2026', sourceUrl: 'https://www.vie-publique.fr/en-bref/300727-comptes-de-la-securite-sociale' },
  ],
  insight: "Depuis 1974, pas un seul budget en équilibre. 50 ans de fuite en avant.",
};

export const BLOC_DEPENSE = {
  id: 'depense_publique',
  title: 'Dépense publique',
  subtitle: "Le poids de l'État",
  color: '#000091',
  icon: 'building',
  kpis: [
    { id: 'depenses_publiques', label: 'Dépenses publiques', value: '58,2', unit: '% PIB', comparison: '~1 740 Mds€ (2025)', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'INSEE Comptes nationaux 2025', sourceUrl: 'https://www.insee.fr/fr/statistiques/2381414' },
    { id: 'prelevements', label: 'Prélèvements obligatoires', value: '43,6', unit: '% PIB', comparison: 'Record OCDE', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'INSEE Comptes nationaux 2025', sourceUrl: 'https://www.insee.fr/fr/statistiques/8956575' },
    { id: 'fonctionnaires', label: 'Agents publics', value: '5,7', unit: 'millions', comparison: '20% des actifs', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'DGAFP, Rapport annuel 2025', sourceUrl: 'https://www.fonction-publique.gouv.fr/les-chiffres-cles' },
    { id: 'retraites_pib', label: 'Retraites / PIB', value: '14,4', unit: '%', comparison: 'vs 10% Allemagne', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'Eurostat ESSPROS 2024', sourceUrl: 'https://ec.europa.eu/eurostat/databrowser/view/spr_exp_sum' },
  ],
  insight: "La France taxe et dépense plus que tous ses voisins, pour des résultats souvent inférieurs.",
};

export const BLOC_INDUSTRIE = {
  id: 'industrie',
  title: 'Industrie & Compétitivité',
  subtitle: 'La désindustrialisation',
  color: '#6B7280',
  icon: 'factory',
  kpis: [
    { id: 'part_industrie', label: 'Part industrie / PIB', value: '10', unit: '%', comparison: 'vs 24% en 1980', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'INSEE / Eurostat SNA 2023', sourceUrl: 'https://www.insee.fr/fr/statistiques/2381416' },
    { id: 'emplois_industriels', label: 'Emplois industriels perdus', value: '-2,5', unit: 'millions', comparison: 'En 40 ans', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'INSEE, séries longues emploi', sourceUrl: 'https://www.insee.fr/fr/statistiques/2012694' },
    { id: 'pib_habitant_ecart', label: 'PIB/hab. vs Allemagne', value: '-10 170', unit: '€', comparison: 'Par habitant/an', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.EMERGENT, source: 'Eurostat nama_10_pc 2025', sourceUrl: 'https://ec.europa.eu/eurostat/databrowser/view/nama_10_pc' },
    { id: 'faillites', label: "Défaillances d'entreprises", value: '69 392', unit: '/12 mois', comparison: 'Record absolu (fév 2026)', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.EMERGENT, source: 'Banque de France, cumul 12 mois à fin février 2026', sourceUrl: 'https://www.banque-france.fr/fr/statistiques/entreprises/defaillances-dentreprises-2026-02' },
  ],
  insight: "2,5 millions d'emplois industriels perdus en 40 ans.",
};

export const BLOC_TRAVAIL = {
  id: 'travail',
  title: 'Travail & Emploi',
  subtitle: 'Le travail ne paie plus',
  color: '#059669',
  icon: 'briefcase',
  kpis: [
    { id: 'taux_emploi', label: "Taux d'emploi 20-64", value: '75,1', unit: '%', comparison: 'vs 81% Allemagne', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'Eurostat lfsi_emp_a 2024', sourceUrl: 'https://ec.europa.eu/eurostat/databrowser/view/lfsi_emp_a' },
    { id: 'heures_travail', label: 'Heures travaillées/an', value: '1 607', unit: 'h', comparison: 'vs 1 349h Allemagne', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'OCDE, Heures moyennes 2023', sourceUrl: 'https://data.oecd.org/emp/hours-worked.htm' },
    { id: 'rsa_allocataires', label: 'Allocataires RSA', value: '1,9', unit: 'million', comparison: 'En hausse', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.EMERGENT, source: 'CAF, données 2024', sourceUrl: 'https://www.caf.fr/nous-connaitre/statistiques' },
    { id: 'pensions_fonctionnaires_bloc', label: 'Pensions fonctionnaires', value: '68', unit: 'Mds€/an', comparison: 'Taux employeur 82%', status: KPI_STATUS.REAL, origin: KPI_ORIGIN.IFRAP, source: 'CAS Pensions 2025', sourceUrl: 'https://www.ifrap.org/retraite/le-rapporteur-du-budget-reconnait-15-milliards-de-deficit-des-pensions-publiques-detat' },
  ],
  insight: "L'État cotise à 82,28% pour ses fonctionnaires vs 16,5% dans le privé.",
};

export const BLOC_NORMES = {
  id: 'normes',
  title: 'Normes & Complexité',
  subtitle: "L'asphyxie administrative",
  color: '#7C3AED',
  icon: 'document',
  kpis: [
    { id: 'normes_vigueur', label: 'Normes en vigueur', value: '400 000', unit: '', comparison: 'Textes applicables', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'SGAE / IFRAP 2024', sourceUrl: 'https://www.ifrap.org' },
    { id: 'code_travail', label: 'Code du travail', value: '3 500', unit: 'pages', comparison: 'vs 800 en Suisse', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'Legifrance / IFRAP', sourceUrl: 'https://www.legifrance.gouv.fr/codes/id/LEGITEXT000006072050' },
    { id: 'cout_complexite', label: 'Coût de la complexité', value: '80', unit: 'Mds€/an', comparison: 'Estimation OCDE', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'OCDE, Étude 2023', sourceUrl: 'https://www.oecd.org/regreform/' },
  ],
  insight: "4 fois plus de temps en paperasse qu'un entrepreneur allemand.",
};

export const BLOC_SECURITE = {
  id: 'securite',
  title: 'Sécurité & Régalien',
  subtitle: 'Les missions abandonnées',
  color: '#DC2626',
  icon: 'shield',
  cautionNote: 'Données à consolider - Affichées à titre indicatif',
  kpis: [
    { id: 'oqtf', label: 'OQTF exécutées', value: '<10', unit: '%', comparison: 'Obligations non respectées', status: KPI_STATUS.TO_VALIDATE, origin: KPI_ORIGIN.IFRAP, source: 'Min. Intérieur 2024', sourceUrl: 'https://www.interieur.gouv.fr' },
    { id: 'surpopulation_carcerale', label: 'Surpopulation carcérale', value: '120', unit: '%', comparison: "Taux d'occupation", status: KPI_STATUS.TO_VALIDATE, origin: KPI_ORIGIN.IFRAP, source: 'DAP, Statistiques mensuelles 2025', sourceUrl: 'https://www.justice.gouv.fr' },
    { id: 'budget_justice', label: 'Budget Justice / PIB', value: '0,2', unit: '%', comparison: 'Dernier rang UE', status: KPI_STATUS.ESTIMATE, origin: KPI_ORIGIN.IFRAP, source: 'PLF 2025 / CEPEJ', sourceUrl: 'https://www.justice.gouv.fr' },
  ],
  insight: "L'État s'est dispersé, abandonnant ses missions régaliennes fondamentales.",
};

export const ALL_BLOCS = [
  BLOC_DETTE,
  BLOC_DEPENSE,
  BLOC_INDUSTRIE,
  BLOC_TRAVAIL,
  BLOC_NORMES,
  BLOC_SECURITE,
];

export default {
  HEADLINE_KPIS,
  ALL_BLOCS,
  KPI_STATUS,
  KPI_STATUS_LABELS,
  KPI_ORIGIN,
};
